"use client";

import { Container } from "@/components/ui/container";
import { SectionHeading } from "@/components/ui/section-heading";
import { Reveal } from "@/components/ui/reveal";
import { Icon } from "@/components/ui/icon";
import { execSummary } from "@/lib/data/homepage";

/**
 * Editorial layout: massive pull-quotes interleaved with narrow body
 * text. Asymmetric, magazine-style, not a card grid.
 */
export function ExecutiveSummary() {
  return (
    <section id="exec-summary" className="relative section-pad">
      <Container>
        <SectionHeading
          eyebrow={execSummary.eyebrow}
          title={execSummary.title}
          icon="Award"
          subtitle=""
        />

        <div className="mt-16 grid lg:grid-cols-[1.2fr_1fr] gap-12">
          {/* LEFT — editorial column with massive stats */}
          <Reveal>
            <div className="space-y-12">
              {/* Lead paragraph */}
              <p className="text-[1.15rem] leading-[1.7] text-[rgb(var(--fg))] max-w-2xl">
                {execSummary.paragraphs[0]}
              </p>

              {/* Pull-quote 1 */}
              <div className="border-l-4 pl-6 py-2" style={{ borderColor: "var(--color-crimson)" }}>
                <div className="display-stat" style={{ color: "var(--color-crimson)" }}>
                  87%
                </div>
                <p className="mt-3 text-body leading-relaxed text-[rgb(var(--fg-muted))] max-w-xl">
                  of consumers research brands online before engagement. <span className="text-[rgb(var(--fg))] font-semibold">60%</span> form opinions from search results alone.
                </p>
              </div>

              <h3>{execSummary.whyTitle}</h3>
              <p className="text-body leading-[1.7] text-[rgb(var(--fg-muted))] max-w-2xl">
                {execSummary.why}
              </p>

              {/* Pull-quote 2 */}
              <div className="rounded-2xl glass p-6 card-glow max-w-xl" style={{ ["--glow" as string]: "var(--color-purple)" }}>
                <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] mb-2">
                  The Shift
                </div>
                <p className="text-body leading-relaxed text-[rgb(var(--fg))]">
                  Perception is not a byproduct of operations — <span style={{ color: "var(--color-purple)" }} className="font-semibold">it IS the operation.</span>
                </p>
              </div>
            </div>
          </Reveal>

          {/* RIGHT — audience coverage as a multi-row list with stats */}
          <Reveal delay={120}>
            <div className="space-y-3">
              <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] mb-4">
                {execSummary.whoTitle}
              </div>
              {execSummary.audiences.map((a, i) => (
                <div
                  key={a.title}
                  className="group flex items-start gap-4 rounded-xl border border-[rgb(var(--border)/0.15)] bg-[rgb(var(--surface)/0.4)] hover:bg-[rgb(var(--surface)/0.7)] px-4 py-3.5 transition-all"
                >
                  <span
                    className="grid place-items-center h-10 w-10 rounded-lg shrink-0 transition-transform group-hover:scale-110"
                    style={{
                      background: `linear-gradient(135deg, color-mix(in oklch, var(--color-${a.icon === 'Landmark' ? 'crimson' : a.icon === 'Building2' ? 'purple' : a.icon === 'Vote' ? 'amber' : a.icon === 'Crown' ? 'gold' : a.icon === 'Shield' ? 'emerald' : 'cyan'}) 18%, transparent)`,
                      boxShadow: "inset 0 0 0 1px rgb(var(--border) / 0.18)",
                    }}
                  >
                    <Icon name={a.icon} className="h-4 w-4" style={{ color: "var(--fg)" }} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[0.97rem] leading-tight">{a.title}</div>
                    <div className="text-[0.82rem] text-[rgb(var(--fg-muted))] mt-0.5 leading-snug">
                      {a.desc}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}
